function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

/**
 * Lazy comparison functions taking into account specific properties of objects.
 * For example, arrays of objects are not processed.
 * @param {*} obj1 The first object to compare.
 * @param {*} obj2 The second object to compare.
 */
const deepObjectDiffs = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const diffs = {};
  Object.keys(obj2).forEach((k) => {
    const index = keys1.indexOf(k);
    const val2 = obj2[k];
    if (index >= 0) {
      keys1.splice(index, 1);
      const val1 = obj1[k];
      let eq;
      if (val2 === undefined || val2 === null) {
        eq = val1 === val2;
      } else if (typeof val2 === 'string' || val2 instanceof String) {
        eq = val1 === val2;
      } else if (typeof val2 === 'object') {
        if (!val1) {
          diffs[k] = val2;
          eq = true; // diffs is already updated
        } else {
          const d = deepObjectDiffs(val1, val2);
          if (d) {
            diffs[k] = d;
          }
          eq = true; // diffs is already updated
        }
      } else {
        eq = val1 === val2;
      }
      if (!eq) {
        diffs[k] = val2;
      }
    } else {
      diffs[k] = val2;
    }
  });
  keys1.forEach((k) => {
    diffs[k] = null;
  });
  return !isEmpty(diffs) && diffs;
};

const computeArrayDiffs = (oldArray, newArray, objectsId, options = {}) => {
  let { objectsById } = options;
  const getId = typeof objectsId === 'string' ? obj => obj[objectsId] : objectsId;
  const newObjects = [];
  const deletedObjects = [];
  const changedObjectsById = {};
  let changedObjectCount = 0;
  const { objectDiffs = deepObjectDiffs } = options;

  if (!objectsById) {
    objectsById = {};
    oldArray.forEach((o, i) => {
      objectsById[getId(o, i)] = o;
    });
  }
  const notVisitedObjectsById = Object.assign({}, objectsById);
  newArray.forEach((o, i) => {
    const id = getId(o, i);
    const oldObj = notVisitedObjectsById[id];
    if (!oldObj) {
      newObjects.push(o);
      objectsById[id] = o;
    } else {
      delete notVisitedObjectsById[id];
      const objDiffs = objectDiffs(oldObj, o);

      if (objDiffs) {
        objectsById[id] = o;
        changedObjectsById[id] = { old: oldObj, new: o, diffs: objDiffs };
        changedObjectCount += 1;
      } else {
        // If no diffs, keep old object to optimize redraw of components depending on it
        newArray[i] = oldObj;
      }
    }
  });
  Object.keys(notVisitedObjectsById).forEach((id) => {
    deletedObjects.push(notVisitedObjectsById[id]);
    delete objectsById[id];
  });
  return !changedObjectCount && !deletedObjects.length && !newObjects.length
    ? null
    : {
      newObjects: newObjects || undefined,
      deletedObjects: deletedObjects || undefined,
      changedObjects: changedObjectCount ? changedObjectsById : undefined,
    };
};

const sameValueArrays = (arr1 = [], arr2 = []) => {
  if (arr1.length !== arr2.length) return false;
  return !arr1.find(o1 => arr2.indexOf(o1) < 0);
};

export { deepObjectDiffs, computeArrayDiffs, sameValueArrays };
